import * as i0 from '@angular/core';
import { Injectable, ɵNG_PIPE_DEF, ErrorHandler as ErrorHandler$1, Pipe, NgModule } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { __decorate, __metadata } from 'tslib';
import { Auth } from '@angular/fire/auth';
import { Messaging } from '@angular/fire/messaging';
import { ref, uploadString, uploadBytes, getDownloadURL, Storage } from '@angular/fire/storage';
import { httpsCallableData, Functions } from '@angular/fire/functions';
import { FirestoreService, Autowire } from 'simplyfire/ngx';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/platform-browser';
import { isValid, isYesterday, formatDistanceToNow } from 'date-fns';
import { Timestamp } from '@angular/fire/firestore';

// @dynamic
class ApplicationContext {
    static get() {
        return ApplicationContext.instance || new this();
    }
    initialize(options) {
        if (!options) {
            throw Error('Application is not initialized');
        }
        ApplicationContext.injector = options.injector;
    }
}
ApplicationContext.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ApplicationContext, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ApplicationContext.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ApplicationContext, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ApplicationContext, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const NG_PIPE_DEF = ɵNG_PIPE_DEF;
function isPipe(target) {
    return !!target[NG_PIPE_DEF];
}

/**
 * Applied to instances and stores `Subject` instance when
 * no custom destroy method is provided.
 */
const DESTROY = Symbol('__destroy');
/**
 * Applied to definitions and informs that class is decorated
 */
const DECORATOR_APPLIED = Symbol('__decoratorApplied');
/**
 * If we use the `untilDestroyed` operator multiple times inside the single
 * instance providing different `destroyMethodName`, then all streams will
 * subscribe to the single subject. If any method is invoked, the subject will
 * emit and all streams will be unsubscribed. We wan't to prevent this behavior,
 * thus we store subjects under different symbols.
 */
function getSymbol(destroyMethodName) {
    if (typeof destroyMethodName === 'string') {
        return Symbol(`__destroy__${destroyMethodName}`);
    }
    else {
        return DESTROY;
    }
}
function markAsDecorated(type) {
    // Store this property on the prototype if it's an injectable class, component or directive.
    // We will be able to handle class extension this way.
    type.prototype[DECORATOR_APPLIED] = true;
}
function createSubjectOnTheInstance(instance, symbol) {
    if (!instance[symbol]) {
        instance[symbol] = new Subject();
    }
}
function completeSubjectOnTheInstance(instance, symbol) {
    if (instance[symbol]) {
        instance[symbol].next();
        instance[symbol].complete();
        // We also have to re-assign this property thus in the future
        // we will be able to create new subject on the same instance.
        instance[symbol] = null;
    }
}

function unsubscribe(property) {
    if (property instanceof Subscription) {
        property.unsubscribe();
    }
}
function unsubscribeIfPropertyIsArrayLike(property) {
    Array.isArray(property) && property.forEach(unsubscribe);
}
function decorateNgOnDestroy(ngOnDestroy, options) {
    return function () {
        // Invoke the original `ngOnDestroy` if it exists
        ngOnDestroy && ngOnDestroy.call(this);
        // It's important to use `this` instead of caching instance
        // that may lead to memory leaks
        completeSubjectOnTheInstance(this, getSymbol());
        // Check if subscriptions are pushed to some array
        if (options.arrayName) {
            return unsubscribeIfPropertyIsArrayLike(this[options.arrayName]);
        }
        // Loop through the properties and find subscriptions
        if (options.checkProperties) {
            for (const property in this) {
                if (options.blackList?.includes(property)) {
                    continue;
                }
                unsubscribe(this[property]);
            }
        }
    };
}
function decorateProviderDirectiveOrComponent(type, options) {
    type.prototype.ngOnDestroy = decorateNgOnDestroy(type.prototype.ngOnDestroy, options);
}
function decoratePipe(type, options) {
    const def = type.ɵpipe;
    def.onDestroy = decorateNgOnDestroy(def.onDestroy, options);
}
function UntilDestroy(options = {}) {
    return (type) => {
        if (isPipe(type)) {
            decoratePipe(type, options);
        }
        else {
            decorateProviderDirectiveOrComponent(type, options);
        }
        markAsDecorated(type);
    };
}

function overrideNonDirectiveInstanceMethod(instance, destroyMethodName, symbol) {
    const originalDestroy = instance[destroyMethodName];
    if (ngDevMode && typeof originalDestroy !== 'function') {
        throw new Error(`${instance.constructor.name} is using untilDestroyed but doesn't implement ${destroyMethodName}`);
    }
    createSubjectOnTheInstance(instance, symbol);
    instance[destroyMethodName] = function () {
        // eslint-disable-next-line prefer-rest-params
        originalDestroy.apply(this, arguments);
        completeSubjectOnTheInstance(this, symbol);
        // We have to re-assign this property back to the original value.
        // If the `untilDestroyed` operator is called for the same instance
        // multiple times, then we will be able to get the original
        // method again and not the patched one.
        instance[destroyMethodName] = originalDestroy;
    };
}
function untilDestroyed(instance, destroyMethodName) {
    return (source) => {
        const symbol = getSymbol(destroyMethodName);
        // If `destroyMethodName` is passed then the developer applies
        // this operator to something non-related to Angular DI system
        if (typeof destroyMethodName === 'string') {
            overrideNonDirectiveInstanceMethod(instance, destroyMethodName, symbol);
        }
        else {
            ngDevMode && ensureClassIsDecorated(instance);
            createSubjectOnTheInstance(instance, symbol);
        }
        return source.pipe(takeUntil(instance[symbol]));
    };
}
function ensureClassIsDecorated(instance) {
    const prototype = Object.getPrototypeOf(instance);
    const missingDecorator = !(DECORATOR_APPLIED in prototype);
    if (missingDecorator) {
        throw new Error('untilDestroyed operator cannot be used inside directives or ' +
            'components or providers that are not decorated with UntilDestroy decorator');
    }
}

// Fork of @ngneat/until-destroy

class FirebaseService extends FirestoreService {
    async uploadFile(data, name, bucket = 'images') {
        const storageRef = ref(this.storage, `${bucket}/${name}`);
        const uploadResult = await (typeof data === 'string'
            ? uploadString(storageRef, data, 'data_url')
            : uploadBytes(storageRef, data));
        return await getDownloadURL(uploadResult.ref);
    }
    /**
     * HttpsCallable
     */
    callHttpsFunc(name, data = {}, options) {
        return httpsCallableData(this.functions, name, options)(data).toPromise();
    }
}
FirebaseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: FirebaseService, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
FirebaseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: FirebaseService, providedIn: 'root' });
__decorate([
    Autowire(),
    __metadata("design:type", Auth)
], FirebaseService.prototype, "auth", void 0);
__decorate([
    Autowire(),
    __metadata("design:type", Storage)
], FirebaseService.prototype, "storage", void 0);
__decorate([
    Autowire(),
    __metadata("design:type", Messaging)
], FirebaseService.prototype, "messaging", void 0);
__decorate([
    Autowire(),
    __metadata("design:type", Functions)
], FirebaseService.prototype, "functions", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: FirebaseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], propDecorators: { auth: [], storage: [], messaging: [], functions: [] } });

class CkuploadAdapter {
    constructor(loader) {
        this.loader = loader;
    }
    async upload() {
        const { file } = this.loader;
        const downloadURL = await this.firebaseService.uploadFile(file, file.name, 'images');
        return { default: downloadURL };
    }
}
__decorate([
    Autowire(),
    __metadata("design:type", FirebaseService)
], CkuploadAdapter.prototype, "firebaseService", void 0);

class ErrorHandler extends ErrorHandler$1 {
    handleError(error) {
        error = error.rejection || error;
        console.log(error);
    }
}
ErrorHandler.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ErrorHandler, deps: null, target: i0.ɵɵFactoryTarget.Injectable });
ErrorHandler.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ErrorHandler, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ErrorHandler, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SanitizeHtmlPipe {
    constructor(sanitizer, renderer) {
        this.sanitizer = sanitizer;
        this.renderer = renderer;
    }
    transform(value, ...args) {
        const element = this.renderer.createElement('textarea');
        element.innerHTML = value;
        return this.sanitizer.bypassSecurityTrustHtml(element.textContent);
    }
}
SanitizeHtmlPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: SanitizeHtmlPipe, deps: [{ token: i1.DomSanitizer }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Pipe });
SanitizeHtmlPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: SanitizeHtmlPipe, name: "sanitizeHtml" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: SanitizeHtmlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitizeHtml'
                }]
        }], ctorParameters: function () { return [{ type: i1.DomSanitizer }, { type: i0.Renderer2 }]; } });

// chunk array to a certain size
const arrayToChunks = (list, size) => {
    list = [...list];
    return [...Array(Math.ceil(list.length / size))].map((_) => list.splice(0, size));
};

const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});
const htmlToText = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};
const shortNumber = (value) => {
    if (!value) {
        return 0;
    }
    if (value < 1000) {
        return value;
    }
    const powers = [
        { key: 'Q', step: Math.pow(10, 15) },
        { key: 'T', step: Math.pow(10, 12) },
        { key: 'B', step: Math.pow(10, 9) },
        { key: 'M', step: Math.pow(10, 6) },
        { key: 'K', step: 1000 }
    ];
    for (const { key, step } of powers) {
        const reduced = value / step;
        if (reduced >= 1) {
            return Math.round(reduced) + key;
        }
    }
    return value;
};

class ShortNumberPipe {
    transform(value, ...args) {
        return shortNumber(value);
    }
}
ShortNumberPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ShortNumberPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
ShortNumberPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: ShortNumberPipe, name: "shortNumber" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: ShortNumberPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'shortNumber'
                }]
        }] });

class TimeAgoPipe {
    transform(value, ...args) {
        if (value instanceof Timestamp) {
            value = value.toDate();
        }
        if (!isValid(value)) {
            return '';
        }
        return isYesterday(value) ? 'Yesterday' : formatDistanceToNow(value, { addSuffix: true });
    }
}
TimeAgoPipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: TimeAgoPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
TimeAgoPipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: TimeAgoPipe, name: "timeAgo" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: TimeAgoPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'timeAgo'
                }]
        }] });

const pipes = [SanitizeHtmlPipe, ShortNumberPipe, TimeAgoPipe];
class PipesModule {
}
PipesModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: PipesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PipesModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.1.0", ngImport: i0, type: PipesModule, declarations: [SanitizeHtmlPipe, ShortNumberPipe, TimeAgoPipe], imports: [CommonModule], exports: [SanitizeHtmlPipe, ShortNumberPipe, TimeAgoPipe] });
PipesModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: PipesModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.1.0", ngImport: i0, type: PipesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [pipes],
                    imports: [CommonModule],
                    exports: [pipes]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ApplicationContext, CkuploadAdapter, ErrorHandler, FirebaseService, PipesModule, SanitizeHtmlPipe, ShortNumberPipe, TimeAgoPipe, UntilDestroy, arrayToChunks, htmlToText, readFileAsDataURL, shortNumber, untilDestroyed };
